import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { permissionGuard } from './core/auth/permission.guard';
import { roleGuard } from './core/auth/role.guard';
import { Role } from './core/models/role.enum';
import { Layout } from './shared/components/layout/layout';

export const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.authRoutes),
  },
  {
    path: '',
    component: Layout,
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
      },
      {
        path: 'stats',
        canActivate: [roleGuard],
        data: { roles: [Role.ADMIN] },
        loadChildren: () => import('./features/stats/stats.routes').then((m) => m.STATS_ROUTES),
      },
      {
        path: 'passengers',
        loadChildren: () => import('./features/passengers/passengers.routes').then((m) => m.passengersRoutes),
      },
      {
        path: 'drivers',
        loadChildren: () =>
          import('./features/drivers/drivers.routes').then((m) => m.driversRoutes),
      },
      {
        path: 'trips',
        loadChildren: () => import('./features/trips/trips.routes').then((m) => m.tripsRoutes),
      },
      {
        path: 'bookings',
        loadChildren: () =>
          import('./features/bookings/bookings.routes').then((m) => m.bookingsRoutes),
      },
      {
        path: 'service-requests',
        loadChildren: () =>
          import('./features/service-requests/service-requests.routes').then((m) => m.serviceRequestsRoutes),
      },
      {
        path: 'ratings',
        loadChildren: () =>
          import('./features/ratings/ratings.routes').then((m) => m.ratingsRoutes),
      },
      {
        path: 'reviews',
        loadChildren: () =>
          import('./features/reviews/reviews.routes').then((m) => m.reviewsRoutes),
      },
      {
        path: 'compliance',
        canActivate: [permissionGuard],
        data: { permission: 'COMPLIANCE_READ' },
        loadChildren: () =>
          import('./features/compliance/compliance.routes').then((m) => m.complianceRoutes),
      },
      {
        path: 'compliance-review',
        redirectTo: 'compliance/reviews',
        pathMatch: 'full',
      },
      {
        path: 'documents',
        redirectTo: 'compliance',
        pathMatch: 'full',
      },
      {
        path: 'documents/:id/verify',
        redirectTo: 'compliance',
      },
      {
        path: 'geofencing',
        loadChildren: () =>
          import('./features/geofencing/geofencing.routes').then((m) => m.geofencingRoutes),
      },
      {
        path: 'payments',
        canActivate: [roleGuard],
        data: { roles: [Role.ADMIN] },
        loadChildren: () =>
          import('./features/payments/payments.routes').then((m) => m.paymentsRoutes),
      },
      {
        path: 'subscriptions',
        loadChildren: () =>
          import('./features/subscriptions/subscriptions.routes').then((m) => m.subscriptionsRoutes),
      },
      {
        path: 'fees',
        canActivate: [roleGuard],
        data: { roles: [Role.ADMIN] },
        loadChildren: () => import('./features/fees/fees.routes').then((m) => m.feesRoutes),
      },
      {
        path: 'categories',
        loadChildren: () =>
          import('./features/categories/categories.routes').then((m) => m.categoriesRoutes),
      },
      {
        path: 'locations',
        loadChildren: () =>
          import('./features/locations/locations.routes').then((m) => m.locationsRoutes),
      },
      {
        path: 'geo',
        loadChildren: () => import('./features/geo/geo.routes').then((m) => m.geoRoutes),
      },
      {
        path: 'promos',
        loadChildren: () => import('./features/promos/promos.routes').then((m) => m.promosRoutes),
      },
      {
        path: 'employees',
        loadChildren: () =>
          import('./features/employees/employees.routes').then((m) => m.employeesRoutes),
      },
      {
        path: 'vendors',
        loadChildren: () =>
          import('./features/employees/vendors.routes').then((m) => m.vendorsRoutes),
      },
      {
        path: 'gatekeeper',
        canActivate: [permissionGuard],
        data: { permission: 'AIRPORT_READ' },
        loadChildren: () =>
          import('./features/airport-visit-history/airport-visit-history.routes').then((m) => m.airportVisitHistoryRoutes),
      },
      {
        path: 'airports',
        canActivate: [permissionGuard],
        data: { permission: 'AIRPORT_READ' },
        loadChildren: () =>
          import('./features/airports/airports.routes').then((m) => m.airportRoutes),
      },
      {
        path: 'tracking',
        loadChildren: () =>
          import('./features/tracking/tracking.routes').then((m) => m.trackingRoutes),
      },
      {
        path: 'dispatch',
        loadChildren: () =>
          import('./features/dispatch/dispatch.routes').then((m) => m.dispatchRoutes),
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./features/notifications/notifications.routes').then((m) => m.notificationsRoutes),
      },
      {
        path: 'requests',
        loadChildren: () =>
          import('./features/requests/requests.routes').then((m) => m.requestsRoutes),
      },
      {
        path: 'activity',
        loadChildren: () =>
          import('./features/activity/activity.routes').then((m) => m.activityRoutes),
      },
      {
        path: 'payouts',
        canActivate: [roleGuard],
        data: { roles: [Role.ADMIN] },
        loadChildren: () =>
          import('./features/payouts/payouts.routes').then((m) => m.payoutsRoutes),
      },
      {
        path: 'team',
        canActivate: [roleGuard],
        data: { roles: [Role.ADMIN] },
        loadChildren: () =>
          import('./features/team/team.routes').then((m) => m.teamRoutes),
      },
      {
        path: 'settings',
        canActivate: [roleGuard],
        data: { roles: [Role.ADMIN, Role.TECH] },
        loadChildren: () =>
          import('./features/settings/settings.routes').then((m) => m.settingsRoutes),
      },
      {
        // Monitoring & Health. The feature's own routes declare every dedicated path
        // above the generic /monitoring/:subsystemKey entry, and each carries its own
        // permission guard mirroring the endpoint's @PreAuthorize.
        path: 'monitoring',
        canActivate: [permissionGuard],
        data: { permission: 'MONITORING_VIEW' },
        loadChildren: () =>
          import('./features/monitoring/monitoring.routes').then((m) => m.monitoringRoutes),
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
